import React, { useRef, useState, useEffect, useContext } from 'react'
import { components, constants, useServices } from 'cng-web-lib'
import { Grid } from '@material-ui/core'
import pathMap from 'src/paths/pathMap'
import A6aApiUrls from 'src/apiUrls/A6aApiUrls'
import ConfirmDialog from 'src/views/common/ConfirmDialog'
import CustomButtonSection from 'src/views/common/CustomButtonSection'
import { FileForUserGetUserDetails, GetLoggedInUserPartyName } from '../../common/FileForUserCommon'
import FormProperties from './FormProperties'
import ChargeTypeDialog from 'src/views/common/ChargeTypeDialog'
import ErrorMessageDialog from 'src/views/common/ErrorMessageDialog'
import { useFormContext } from 'react-hook-form'
import { A6ATranslationContext } from './contexts/A6ATranslationContext'

const {
  button: { CngButton },
  form: { CngEditForm },
  table: { useFetchCodeMaintenanceLookup }
} = components

const { filter: { EQUAL } } = constants

function A6aEditPage(props) {
  const { history, onSetLoading, showNotification, id, setCCNValue } = props
  const serverDataRef = useRef()
  const { securedSendRequest } = useServices()

  const [isSuperParty, setSuperParty] = useState(false);
  const [ccnHousebillValidation, setCcnHousebillValidation] = useState(false);
  const [ccnHousebillLimit, setCcnHousebillLimit] = useState(1500);
  const [chargeTypes, setChargeTypes] = useState([])
  const [submitDialog, setSubmitDialog] = useState({ open: false, items: {} })
  const [errorMessageDialog, setErrorMessageDialog] = useState({ open: false, errorMessages: [] })

  const fetchCodeMaintenanceLookup = useFetchCodeMaintenanceLookup()
  const { getTranslatedText } = useContext(A6ATranslationContext)
  
  useEffect(() => {
    Promise.all([
      // Charge type
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [{ field: 'codeType', operator: EQUAL, value: 'BILLING_CHARGE_TYPE' }],
        undefined,
        'code'
      ),
      // Super party
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [{ field: 'codeType', operator: EQUAL, value: 'BILLING_SUPER_PARTY' }],
        undefined, 'code'
      ),
      // CNN Housebill validation
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [{ field: 'codeType', operator: EQUAL, value: 'CCN_HOUSEBILL_VALIDATION' }],
        undefined, 'code'
      )
    ]).then(
      ([billingChargeTypes, billingSuperParty, ccnHousebill]) => {
        compileChargeTypes(billingChargeTypes)

        Object.keys(billingSuperParty).forEach(e => {
          if (e == GetLoggedInUserPartyName()) {
            setSuperParty(true);
          }
        })

        if (ccnHousebill) {
          Object.entries(ccnHousebill).forEach((e) => {
            if (e[0] === 'VALIDATION' && e[1].toLowerCase() === 'true') {
              setCcnHousebillValidation(true);
            } else if (e[0] === 'ACI_OCEAN_A6A_S10') {
              if (/^[+-]?\d+$/.test(e[1])) {
                const keyInt = parseInt(e[1], 10);
                setCcnHousebillLimit(keyInt);
              }
            }
          })
        }
      }
    )
  }, [])

  function compileChargeTypes(billingChargeTypes) {
    var jsonArray = [];
    if (billingChargeTypes) {
      Object.entries(billingChargeTypes).forEach((e) => {
        var jsonObject = { code: e[0], desc: e[1] }
        jsonArray.push(jsonObject)
      })
    }
    setChargeTypes(jsonArray)
  }

  function preSubmitA6A(data) {
    if (data && data.taciOcnA6aContainers) {
      const updatedContainers = data.taciOcnA6aContainers.map(cont =>
        cont.containertypeSize === 'OTHR' ? { ...cont, containertypeSize: cont.containertypeSizeOth, othContainertypeSize: true } : { ...cont, othContainertypeSize: false }
      )
      data.taciOcnA6aContainers = updatedContainers;
    }

    // If submitting a template record to customs, strip the template flag and name
    if (data.templateFlag === true) {
      data.templateFlag = false
      data.templateName = ''
    }

    if(ccnHousebillValidation && data.docFormat === '687' && data.taciOcnA6aShipments.length > ccnHousebillLimit) { 
      window.alert(getTranslatedText('A6A', 'ccnHouseBillOverLimitMsg').replace('{}', ccnHousebillLimit))
    }
    else if (isSuperParty) {
      setSubmitDialog({ open: true, items: data })
    } else {
      data.chargeAs = 'REGULAR'
      handleSubmitA6A(data)
    }
  }

  function handleSubmitA6A(data) {
    onSetLoading(true)

    const fileForUserDetails = FileForUserGetUserDetails()
    if (fileForUserDetails) {
      const { fileForUserId, fileForUserLoginId, fileForUserPartyId } = fileForUserDetails
      data.fileForUserId = fileForUserId
      data.fileForUserLoginId = fileForUserLoginId
      data.fileForUserPartyId = fileForUserPartyId
    }

    securedSendRequest.execute('POST', A6aApiUrls.SUBMIT, data,
      (response) => {
        let data = response.data
        let errorMessages = []
        if (data.errorMessages != null && data.errorMessages.length > 0) {
          errorMessages.push(data.errorMessages);
          setErrorMessageDialog({ open: true, errorMessages })
        } else {
          showNotification('success', getTranslatedText('A6A', 'submitSuccessfulMsg'))
          history.push(pathMap.A6A_LIST_VIEW)
        }
      },
      (error) => {
        console.error(error)
        showNotification('error', getTranslatedText('A6A', 'submitFailedMsg'))
      },
      () => onSetLoading(false)
    )
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <CngEditForm
          fieldLevel="toBeChangedByDeveloperIfWantFieldLevelRestriction"
          showNotification={showNotification}
          bodySection={
            <FormProperties.Fields disabled={false} showNotification={showNotification} />
          }
          formikProps={FormProperties.formikProps}
          toClientDataFormat={FormProperties.toClientDataFormat}
          toServerDataFormat={FormProperties.toServerDataFormat}
          fetch={{
            url: A6aApiUrls.GET,
            onPreSuccess: (serverData) => {
              serverDataRef.current = serverData
              let fileForUserDetails = FileForUserGetUserDetails();
              if (fileForUserDetails != null && fileForUserDetails != undefined) {
                serverDataRef.current.fileForUserId = fileForUserDetails.fileForUserId;
                serverDataRef.current.fileForUserLoginId = fileForUserDetails.fileForUserLoginId;
                serverDataRef.current.fileForUserPartyId = fileForUserDetails.fileForUserPartyId;
              }
              // Mark as editing a template so template fields become read-only
              if (serverData.templateFlag === true) {
                serverDataRef.current.isEditTemplate = true
              }
              setCCNValue(serverData.ccn)
            }
          }}
          update={{ url: A6aApiUrls.PUT }}
          id={id}
          renderButtonSection={(_, loading) => (
            <A6AEditPageButtonSection
              history={history}
              loading={loading}
              onSetLoading={onSetLoading}
              showNotification={showNotification}
              serverDataRef={serverDataRef}
              isSuperParty={isSuperParty}
              onSubmitA6A={preSubmitA6A}
            />
          )}
        />
      </Grid>
      <ChargeTypeDialog
        chargeTypes={chargeTypes}
        onClose={() => setSubmitDialog({ open: false, items: {} })}
        onSubmit={handleSubmitA6A}
        open={submitDialog.open}
        items={submitDialog.items}
      />
      <ErrorMessageDialog
        errorMessages={errorMessageDialog.errorMessages}
        open={errorMessageDialog.open}
        onClose={() => setErrorMessageDialog({ open: false })} />
    </Grid>
  )
}

export default A6aEditPage

function A6AEditPageButtonSection(props) {
  const { history, loading, onSetLoading, showNotification, serverDataRef, onSubmitA6A } = props
  const { getValues } = useFormContext()
  const { getTranslatedText } = useContext(A6ATranslationContext)

  const [confirmDialog, setConfirmDialog] = useState(false)

  useEffect(() => {
    onSetLoading(loading)
  }, [loading])

  return (
    <>
      <Grid container justify='space-between' spacing={3}>
        <Grid item xs='auto'>
          <CngButton color='secondary' disabled={loading} onClick={() => setConfirmDialog(true)} size='medium'>
              {getTranslatedText('A6A', 'discardButton')}
            </CngButton>
        </Grid>
        <Grid item xs='auto'>
          <Grid container spacing={2}>
            <Grid item xs='auto'>
              <CngButton color='primary' disabled={loading} type='submit' size='medium' shouldShowProgress={loading}>
                {getTranslatedText('A6A', 'updateButton')}
              </CngButton>
            </Grid>
            <Grid item xs='auto'>
              <CngButton color='primary' disabled={loading} size='medium' onClick={() => onSubmitA6A(getValues())}>{getTranslatedText('A6A', 'submitButton')}</CngButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ConfirmDialog
        isConfirmDialogOpen={confirmDialog}
        closeDialog={() => setConfirmDialog(false)}
        confirmDialog={() => history.push(pathMap.A6A_LIST_VIEW)}
        content={getTranslatedText('A6A', 'discardContent')}
        okMsg={getTranslatedText('A6A', 'discardOkMsg')}
        cancelMsg={getTranslatedText('A6A', 'discardCancelMsg')}
        title={getTranslatedText('A6A', 'discardTitle')}
      />
    </>
  )
}
